import { Image, useMuiTheme } from "@avenue-8/sidekick-ui";
import { dimensions } from "./helpers";
import { useCallback, useMemo } from "react";

const CDN_URL = "https://ucarecdn.com/";

export interface ResponsiveImageProps {
  src: string;
  alt: string;
  loading?: "lazy" | "eager";
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  size?: ResponsiveImageDimensionType;
}

export type ResponsiveImageDimensionType = "xs" | "sm" | "md" | "lg" | "xl";

const isUploadcareURL = (url: string) => url.startsWith(CDN_URL);

export const ResponsiveImage = ({
  src,
  alt,
  width,
  height,
  size,
  ...rest
}: ResponsiveImageProps) => {
  const theme = useMuiTheme();
  const breakpointsValues = theme.breakpoints.values;

  const defaultValues = useMemo(() => {
    let referenceWidth = window.innerWidth;
    if (size && breakpointsValues[size] < window.innerWidth) {
      referenceWidth = breakpointsValues[size];
    }

    if (referenceWidth >= breakpointsValues.xl) {
      return dimensions.xl;
    }
    if (referenceWidth >= breakpointsValues.lg) {
      return dimensions.lg;
    }
    if (referenceWidth >= breakpointsValues.md) {
      return dimensions.md;
    }
    if (referenceWidth >= breakpointsValues.sm) {
      return dimensions.sm;
    }
    return dimensions.xs;
  }, [breakpointsValues, size]);

  const buildPreviewParams = useCallback(() => {
    if (width && height) {
      return `${width}x${height}`;
    }

    if ((width && !height) || (!width && height)) {
      console.warn(
        "ResponsiveImage: Both width and height must be specified. Using default values."
      );
    }

    return `${defaultValues.width}x${defaultValues.height}`;
  }, [defaultValues, width, height]);

  const imageSrc = useMemo(() => {
    if (src && isUploadcareURL(src)) {
      const urlWithoutTrailingSlash = src.endsWith("/")
        ? src.slice(0, -1)
        : src;
      const newSrc = `${urlWithoutTrailingSlash}/-/preview/${buildPreviewParams()}/`;
      return newSrc;
    } else {
      return src;
    }
  }, [src, buildPreviewParams]);

  return <Image src={imageSrc} alt={alt} {...rest} />;
};
